import React, { useEffect, useMemo, useCallback, memo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { usePageTitle } from 'utils/pageTitle';
import { useOrganizations } from 'data/repositories/organizationsRepository';
import { openModal } from 'data/reducers/modalReducer';
import { showToast } from 'data/reducers/toastReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import DashboardLayout from 'pages/_rd_dashboard/DashboardLayout';
import Spinner from 'components/Spinner';
import Card from 'components/Card/Card';
import './OrganizationMembersPage.css';

// Memoized Member Row Component
const MemberRow = memo(
	({ member, currentUserId, userRole, onUpdateRole, onRemove }) => {
		const isCurrentUser = useMemo(
			() => member.id === currentUserId,
			[member.id, currentUserId],
		);

		const isOwner = useMemo(() => member.role === 'owner', [member.role]);

		// Used by the member actions column, which is commented out below for now
		// eslint-disable-next-line no-unused-vars
		const canManage = useMemo(
			() => userRole === 'owner' && !isOwner && !isCurrentUser,
			[userRole, isOwner, isCurrentUser],
		);

		const roleBadgeClass = useMemo(() => {
			switch (member.role) {
				case 'owner':
					return 'bg-primary';
				case 'admin':
					return 'bg-info';
				case 'member':
					return 'bg-secondary';
				default:
					return 'bg-light text-dark';
			}
		}, [member.role]);

		return (
			<tr>
				<td>
					<div className="d-flex align-items-center">
						<div className="organization-members__avatar me-3">
							{member.name.charAt(0).toUpperCase()}
						</div>
						<div>
							<div className="fw-bold">{member.name}</div>
							<div className="text-muted small">{member.email}</div>
						</div>
					</div>
				</td>
				<td>
					<span className={`badge ${roleBadgeClass}`}>
						{member.role}
						{isCurrentUser && ' (You)'}
					</span>
				</td>
				<td>
					{member.added_by_user_name ? (
						<span className="text-muted small">
							Added by {member.added_by_user_name}
						</span>
					) : (
						<span className="text-muted small">—</span>
					)}
				</td>
				<td>
					<span className="text-muted small">{member.joined_at}</span>
				</td>
				{/* <td>
					{canManage && (
						<div className="btn-group btn-group-sm">
							<button
								className="btn btn-outline-secondary"
								onClick={() => onUpdateRole(member)}
							>
								<i className="bi bi-pencil"></i>
							</button>
							<button
								className="btn btn-outline-danger"
								onClick={() => onRemove(member.id)}
							>
								<i className="bi bi-trash"></i>
							</button>
						</div>
					)}
				</td> */}
			</tr>
		);
	},
);

MemberRow.displayName = 'MemberRow';

// Main Component
const OrganizationMembersPage = memo(() => {
	const { id } = useParams();
	const history = useHistory();
	const dispatch = useDispatch();

	const {
		selectedItem: organization,
		members,
		isLoadingMembers,
		actions,
	} = useOrganizations();

	usePageTitle(`Organization Members - ${organization?.name || 'Loading...'}`);

	// Load organization and members
	useEffect(() => {
		if (id) {
			actions.fetchOne(id);
			actions.getMembers({ id });
		}
	}, [id, actions]);

	// Memoized current user ID
	const currentUserId = useMemo(() => {
		// Get from Redux user state or context
		return 1; // TODO: Get from actual user state
	}, []);

	// Memoized user role in this organization
	const userRole = useMemo(() => {
		return organization?.user_role || 'viewer';
	}, [organization]);

	// Memoized members list
	const membersList = useMemo(() => {
		return members?.data || members || [];
	}, [members]);

	// Handlers
	// Used by the "Add Member" button, which is commented out below for now
	// eslint-disable-next-line no-unused-vars
	const handleAddMember = useCallback(() => {
		dispatch(
			openModal(MODAL_TYPES.ADD_ORGANIZATION_MEMBER, {
				organizationId: id,
				onSuccess: () => {
					actions.getMembers({ id });
				},
			}),
		);
	}, [dispatch, id, actions]);

	const handleUpdateRole = useCallback(
		(member) => {
			dispatch(
				openModal(MODAL_TYPES.UPDATE_ORGANIZATION_MEMBER, {
					organizationId: id,
					member,
					onSuccess: () => {
						actions.getMembers({ id });
					},
				}),
			);
		},
		[dispatch, id, actions],
	);

	const handleRemove = useCallback(
		async (userId) => {
			dispatch(
				openModal(MODAL_TYPES.CONFIRM, {
					title: 'Remove Member',
					message:
						'Are you sure you want to remove this member from the organization?',
					confirmLabel: 'Remove',
					color: 'danger',
					onConfirm: async () => {
						try {
							await actions.removeMember({ id, userId });
							dispatch(
								showToast({
									title: 'Success',
									body: 'Member removed successfully',
									type: 'success',
								}),
							);
							actions.getMembers({ id });
						} catch (error) {
							dispatch(
								showToast({
									title: 'Error',
									body: 'Failed to remove member',
									type: 'error',
								}),
							);
						}
					},
				}),
			);
		},
		[actions, dispatch, id],
	);

	const handleBack = useCallback(() => {
		history.push('/dashboard/organizations');
	}, [history]);

	if (!organization || isLoadingMembers) {
		return (
			<DashboardLayout>
				<div className="text-center py-5">
					<Spinner />
				</div>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<div className="organization-members-page v3-scope">
				<div className="dashboard-section">
					<div className="dashboard-section-header">
						<div className="d-flex align-items-center">
							<button
								className="btn btn-link left-arrow-button p-0 me-3 "
								onClick={handleBack}
							>
								<i
									className="bi bi-arrow-left light"
									style={{ color: 'white' }}
								></i>
							</button>
							<h1>
								<i className="bi bi-people me-3" />
								{organization.name} - Members
							</h1>
						</div>
					</div>

					<div className="dashboard-section-body">
						<div className="d-flex align-items-center justify-content-end">
							{/* {userRole === 'owner' || userRole === 'admin' ? (
								<button
									className="btn btn-primary mb-3"
									onClick={handleAddMember}
								>
									<i className="bi bi-person-plus me-2" />
									Add Member
								</button>
							) : null} */}
						</div>
						<Card>
							<div className="table-responsive">
								<table className="table">
									<thead>
										<tr>
											<th>Member</th>
											<th>Role</th>
											<th>Added By</th>
											<th>Joined</th>
											{/* <th>Actions</th> */}
										</tr>
									</thead>
									<tbody>
										{membersList.map((member) => (
											<MemberRow
												key={member.id}
												member={member}
												currentUserId={currentUserId}
												userRole={userRole}
												onUpdateRole={handleUpdateRole}
												onRemove={handleRemove}
											/>
										))}
									</tbody>
								</table>

								{membersList.length === 0 && (
									<div className="text-center text-muted py-5">
										<i className="bi bi-people fs-1 d-block mb-3"></i>
										<p>No members in this organization yet</p>
									</div>
								)}
							</div>
						</Card>
					</div>
				</div>
			</div>
		</DashboardLayout>
	);
});

OrganizationMembersPage.displayName = 'OrganizationMembersPage';

export default OrganizationMembersPage;
